import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useDrive } from '@/hooks/useDrive';
import { useDrivePicker } from '@/hooks/useDrivePicker';
import {
  Loader2, HardDriveUpload, Download, FileText, Cloud, CheckCircle2, FolderOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GenerateDocsDialog = ({ open, onOpenChange, clientId, clientShortName, draftable, onGenerated, savedFolderId, savedFolderName }) => {
  const { connected, connectDrive } = useDrive();
  const { pickFolder, opening } = useDrivePicker();

  // Union of pending + already-drafted, de-duplicated by template_id.
  const items = useMemo(() => {
    const map = new Map();
    (draftable?.pending || []).forEach((d) => map.set(d.template_id, { ...d, drafted: false }));
    (draftable?.already_drafted || []).forEach((d) => {
      if (!map.has(d.template_id)) map.set(d.template_id, { ...d, drafted: true });
    });
    return Array.from(map.values());
  }, [draftable]);

  const [selected, setSelected] = useState(() => new Set());
  const [folderId, setFolderId] = useState(savedFolderId || '');
  const [folderName, setFolderName] = useState(savedFolderName || '');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(null); // 'drive' | 'download' | null

  // Pre-check pending docs only when the dialog transitions closed → open,
  // so a background draftable refetch never resets the user's selection.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setSelected(new Set((draftable?.pending || []).map((d) => d.template_id)));
    }
    wasOpen.current = open;
  }, [open, draftable]);

  // Reflect the client's remembered folder whenever it changes.
  useEffect(() => {
    setFolderId(savedFolderId || '');
    setFolderName(savedFolderName || '');
  }, [savedFolderId, savedFolderName]);

  const choose = async () => {
    try {
      const picked = await pickFolder();
      if (!picked) return;
      setFolderId(picked.id);
      setFolderName(picked.name);
      toast.success(`Destination set to "${picked.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not open the Google Drive picker');
    }
  };

  const allChecked = items.length > 0 && selected.size === items.length;
  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(items.map((i) => i.template_id)));
  };
  const toggleOne = (tid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  };

  const runGenerate = async (destination) => {
    const template_ids = Array.from(selected);
    if (template_ids.length === 0) {
      toast.info('Select at least one document');
      return;
    }
    if (destination === 'drive' && !connected) {
      toast.info('Connect Google Drive first');
      return;
    }
    setBusy(destination);
    try {
      const r = await axios.post(`${API}/crm/clients/${clientId}/generate-docs`, {
        template_ids,
        destination,
        folder_id: destination === 'drive' ? (folderId || null) : null,
        remember_folder: destination === 'drive' && !!folderId && remember,
        as_google_doc: true,
      });
      if (destination === 'drive') {
        toast.success(`Saved ${r.data.uploaded_files?.length || 0} doc(s) to ${folderName || 'Google Drive'}`);
      } else {
        const dl = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${r.data.zip_url}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([dl.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = r.data.zip_url.split('/').pop();
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Generated ${r.data.count} document(s)`);
      }
      if (onGenerated) await onGenerated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate documents');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden rounded-2xl" data-testid="generate-docs-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle className="text-xl font-semibold text-ink flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-primary" /> Generate RA Docs
          </DialogTitle>
          <DialogDescription className="text-ink/60">
            Pick the documents you need, then save them to Google Drive or your computer.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4">
          {/* Select all */}
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-ink">
              <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="gen-select-all" />
              Select all
            </label>
            <span className="text-xs text-ink/50" data-testid="gen-selected-count">{selected.size} selected</span>
          </div>

          {/* Document list */}
          <div className="max-h-[280px] overflow-y-auto space-y-1.5 pr-1" data-testid="generate-docs-list">
            {items.length === 0 && (
              <div className="text-sm text-ink/50 italic py-6 text-center">No RA documents available for this client.</div>
            )}
            {items.map((it) => {
              const checked = selected.has(it.template_id);
              return (
                <label
                  key={it.template_id}
                  data-testid={`gen-doc-${it.template_id}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${checked ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggleOne(it.template_id)} />
                  <FileText className={`w-4 h-4 shrink-0 ${checked ? 'text-primary' : 'text-ink/40'}`} />
                  <span className="text-sm flex-1 text-ink">{it.name}</span>
                  {it.drafted && (
                    <span className="text-[11px] inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="w-3.5 h-3.5" /> drafted
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {/* Drive target */}
          <div className="mt-5 rounded-xl bg-gray-50 border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-ink">
              <Cloud className="w-4 h-4 text-primary" /> Google Drive
            </div>
            {connected ? (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-ink/50">Destination folder</div>
                    {folderId ? (
                      <div className="text-sm font-medium text-ink truncate" data-testid="gen-folder-name">
                        <FolderOpen className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-primary" />
                        {folderName || 'Selected folder'}
                      </div>
                    ) : (
                      <div className="text-[11px] text-ink/45 font-mono" data-testid="gen-folder-auto">
                        RA License Files / Clients / {clientShortName || 'Unnamed'} / RA Doc
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={choose}
                    disabled={opening}
                    className="rounded-xl shrink-0"
                    data-testid="gen-pick-folder-btn"
                  >
                    {opening ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5 mr-1.5" />}
                    {folderId ? 'Change folder' : 'Choose folder'}
                  </Button>
                </div>

                {folderId && (
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-ink/70">
                      <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="gen-remember-folder" />
                      Remember this folder for this client
                    </label>
                    <button
                      type="button"
                      onClick={() => { setFolderId(''); setFolderName(''); }}
                      className="text-xs text-ink/50 hover:text-ink underline"
                      data-testid="gen-reset-folder"
                    >
                      Use automatic folder
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-ink/40">Documents are saved as editable Google Docs. Folders are created automatically if missing.</p>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-ink/50">Not connected yet.</span>
                <Button size="sm" variant="outline" onClick={connectDrive} className="rounded-xl" data-testid="gen-connect-drive">
                  Connect Drive
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          {selected.size === 0 && items.length > 0 && (
            <span className="text-xs text-ink/50 mr-auto" data-testid="gen-select-hint">Select at least one document to continue</span>
          )}
          <Button
            variant="outline"
            onClick={() => runGenerate('download')}
            disabled={!!busy || selected.size === 0}
            className="h-11 rounded-full"
            data-testid="gen-download-btn"
          >
            {busy === 'download' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Save to computer
          </Button>
          <Button
            onClick={() => runGenerate('drive')}
            disabled={!!busy || selected.size === 0 || !connected}
            className="h-11 rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
            data-testid="gen-drive-btn"
          >
            {busy === 'drive' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <HardDriveUpload className="w-4 h-4 mr-2" />}
            Save to Google Drive
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateDocsDialog;
